import React from 'react';
import { Col, Descriptions, Row } from 'antd';
import { METRIC_LABELS, WINDOWED_EARNINGS_CAPTION } from '../constants';
import { formatCount, formatCurrency, formatPercentage, percentOf } from '../utils/formatters';
import { WindowedCaption } from '../Styles';
import type { PerformanceSummary } from '../types';

interface SummaryDetailPanelProps {
  summary: PerformanceSummary;
}

/**
 * The secondary half of a §3.2 metric block: customer mix, refunds, and the
 * window-scoped earnings split.
 *
 * `byStore[]`, `byCode[]` and each leaderboard row all carry the identical
 * `PerformanceSummary` shape, so this renders the detail for any of them — it's the
 * expanded-row body in both the breakdown tables and the leaderboard.
 */
const SummaryDetailPanel: React.FC<SummaryDetailPanelProps> = ({ summary }) => {
  const customers = summary.customers;
  const totalCustomers =
    (customers?.new ?? 0) + (customers?.returning ?? 0) + (customers?.unknown ?? 0);

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={8}>
        <Descriptions
          title="Customers"
          column={1}
          size="small"
          items={[
            {
              key: 'new',
              label: METRIC_LABELS.NEW_CUSTOMERS,
              children: `${formatCount(customers?.new)} (${formatPercentage(
                percentOf(customers?.new, totalCustomers)
              )})`,
            },
            {
              key: 'returning',
              label: METRIC_LABELS.RETURNING_CUSTOMERS,
              children: `${formatCount(customers?.returning)} (${formatPercentage(
                percentOf(customers?.returning, totalCustomers)
              )})`,
            },
            {
              key: 'unknown',
              label: METRIC_LABELS.UNKNOWN_CUSTOMERS,
              children: formatCount(customers?.unknown),
            },
          ]}
        />
      </Col>

      <Col xs={24} md={8}>
        <Descriptions
          title="Refunds"
          column={1}
          size="small"
          items={[
            {
              key: 'count',
              label: METRIC_LABELS.REFUND_COUNT,
              children: formatCount(summary.refunds?.count),
            },
            {
              key: 'subtotal',
              label: METRIC_LABELS.REFUND_SUBTOTAL,
              children: formatCurrency(summary.refunds?.subtotal),
            },
            {
              key: 'backedOut',
              label: 'Earnings backed out',
              children: formatCurrency(summary.refunds?.earningsBackedOut),
            },
          ]}
        />
      </Col>

      <Col xs={24} md={8}>
        <Descriptions
          title={
            <>
              Earnings <WindowedCaption>({WINDOWED_EARNINGS_CAPTION})</WindowedCaption>
            </>
          }
          column={1}
          size="small"
          items={[
            {
              key: 'pending',
              label: METRIC_LABELS.EARNINGS_PENDING,
              children: formatCurrency(summary.earnings?.pending),
            },
            {
              key: 'payable',
              label: METRIC_LABELS.EARNINGS_PAYABLE,
              children: formatCurrency(summary.earnings?.payable),
            },
            {
              key: 'paid',
              label: METRIC_LABELS.EARNINGS_PAID,
              children: formatCurrency(summary.earnings?.paid),
            },
          ]}
        />
      </Col>
    </Row>
  );
};

/**
 * Memoised: re-renders only when its own props change, not whenever the parent
 * screen re-renders for an unrelated filter or fetch.
 */
export default React.memo(SummaryDetailPanel);
